import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiMessageResponse, ApiUserResponse } from '../models/api.models';
import {
  ActivateAccountPayload,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  LoginPayload,
  RefreshTokenPayload,
  RegisterPayload,
  ResetPasswordPayload,
  UpdateNotificationPreferencesPayload,
  UpdateProfilePayload,
  User,
} from '../models/auth.models';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly api = inject(ApiService);

  login(payload: LoginPayload): Observable<ApiUserResponse<User>> {
    return this.api.post<ApiUserResponse<User>>('/auth/login', payload);
  }

  register(payload: RegisterPayload): Observable<ApiUserResponse<User>> {
    return this.api.post<ApiUserResponse<User>>('/auth/register', payload);
  }

  logout(payload: RefreshTokenPayload): Observable<ApiMessageResponse> {
    return this.api.post<ApiMessageResponse>('/auth/logout', payload);
  }

  refresh(payload: RefreshTokenPayload): Observable<ApiUserResponse<User>> {
    return this.api.post<ApiUserResponse<User>>('/auth/refresh', payload);
  }

  me(): Observable<ApiUserResponse<User>> {
    return this.api.get<ApiUserResponse<User>>('/auth/me');
  }

  forgotPassword(payload: ForgotPasswordPayload): Observable<ApiMessageResponse> {
    return this.api.post<ApiMessageResponse>('/auth/forgot-password', payload);
  }

  resetPassword(payload: ResetPasswordPayload): Observable<ApiMessageResponse> {
    return this.api.post<ApiMessageResponse>('/auth/reset-password', payload);
  }

  verifyEmail(id: string, hash: string, params: Record<string, unknown>): Observable<ApiMessageResponse> {
    return this.api.get<ApiMessageResponse>(`/auth/verify-email/${id}/${hash}`, params);
  }

  activateAccount(
    id: string,
    hash: string,
    payload: ActivateAccountPayload,
    params: Record<string, unknown>,
  ): Observable<ApiMessageResponse> {
    return this.api.post<ApiMessageResponse>(`/admin/activate-account/${id}/${hash}`, payload, params);
  }

  resendVerificationEmail(email: string): Observable<ApiMessageResponse> {
    return this.api.post<ApiMessageResponse>('/auth/email/verification-notification', { email });
  }

  updateProfile(payload: UpdateProfilePayload): Observable<ApiUserResponse<User>> {
    return this.api.put<ApiUserResponse<User>>('/auth/profile', payload);
  }

  changePassword(payload: ChangePasswordPayload): Observable<ApiMessageResponse> {
    return this.api.put<ApiMessageResponse>('/auth/password', payload);
  }

  uploadProfileImage(file: File): Observable<ApiUserResponse<User>> {
    const formData = new FormData();
    formData.append('image', file);
    return this.api.post<ApiUserResponse<User>>('/auth/profile/image', formData);
  }

  updateNotificationPreferences(
    payload: UpdateNotificationPreferencesPayload,
  ): Observable<ApiUserResponse<User>> {
    return this.api.put<ApiUserResponse<User>>('/auth/notification-preferences', payload);
  }
}
